import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolType } from '@/containers/symbols/SymbolType'
import Facade from '@/Facade'
import { SoundManager } from '@/managers/sound/SoundManager'
import { CombinationsData, ShowRegularWinningsData, SymbolData, WinCombination } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { Utilities } from '@base/utils/Utilities'
import { BaseChildState } from '@stateMachine/BaseChildState'
import gsap from 'gsap'

export class RegularWinningsState extends BaseChildState {
    private _fallbackOutWaitTimeoutId: number | null = null
    private _showRegularWinDelayTween: gsap.core.Tween | null = null
    private _completeWinFlowDelayTween: gsap.core.Tween | null = null
    private _awaitingOutAnimations: boolean = false
    private _isWinFlowCompleted: boolean = false
    private _flowToken: number = 0

    public canTrigger(): boolean {
        if ((Facade as any).skipFinalWinPresentationAfterHoldAndWin) {
            return false
        }

        return (
            this._model.spinData.winning_combinations &&
            this._model.spinData.winning_combinations[this._model.cascadeIndex].length > 0
        )
    }

    public begin() {
        const flowToken = ++this._flowToken
        let hasOutAnimationWinningSymbol = false
        let hasHighPayWinningSymbol = false

        GameSignals.regularWinOutAnimationsComplete.connect(this.onRegularWinOutAnimationsComplete)
        GameSignals.stopButtonClicked.connect(this.onSkipWinPresentation)
        GameSignals.spinButtonClicked.connect(this.onSkipWinPresentation)
        this.clearPendingCallbacks()
        this._awaitingOutAnimations = false
        this._isWinFlowCompleted = false

        this._showRegularWinDelayTween = gsap.delayedCall(0.3, () => {
            this._showRegularWinDelayTween = null

            if (flowToken !== this._flowToken || this._isWinFlowCompleted) {
                return
            }

            let winAmount: number = 0
            for (const combination of this._model.spinData.winning_combinations[this._model.cascadeIndex]) {
                winAmount += combination.win_currency
            }

            const combinations: WinCombination[] = this._model.spinData.winning_combinations[this._model.cascadeIndex]
            hasOutAnimationWinningSymbol = combinations.some((combination: WinCombination) => {
                const symbolId = +combination.symbol_id
                return (
                    symbolId === SymbolType.HP_1 ||
                    symbolId === SymbolType.HP_2 ||
                    symbolId === SymbolType.HP_3 ||
                    symbolId === SymbolType.HP_4 ||
                    symbolId === SymbolType.LP_1 ||
                    symbolId === SymbolType.LP_2 ||
                    symbolId === SymbolType.LP_3 ||
                    symbolId === SymbolType.LP_4 ||
                    symbolId === SymbolType.LP_5
                )
            })
            hasHighPayWinningSymbol = combinations.some((combination: WinCombination) => {
                const symbolId = +combination.symbol_id
                return (
                    symbolId === SymbolType.HP_1 ||
                    symbolId === SymbolType.HP_2 ||
                    symbolId === SymbolType.HP_3 ||
                    symbolId === SymbolType.HP_4
                )
            })

            this._model.winPositions = []
            combinations.forEach((combination: WinCombination) => {
                combination.symbols_positions.forEach((winPosition: number[]) => {
                    if (
                        !this._model.winPositions.some((position: number[]) => {
                            return position[0] === winPosition[0] && position[1] === winPosition[1]
                        })
                    ) {
                        this._model.winPositions.push(winPosition)
                    }
                })
            })

            const data: ShowRegularWinningsData = {
                combinations,
                win: winAmount,
                bet: this._model.currentBaseBet,
                winPositions: this._model.winPositions,
            }

            this.setSoundIndexes(data)

            // Arm before emit: showRegularWin may complete the out-wait synchronously.
            if (hasOutAnimationWinningSymbol) {
                this._awaitingOutAnimations = true
                // HP win clips are longer than the old 3.2s cut-off; cutting early starts
                // line-by-line while the total-win anim is still playing.
                const fallbackMs = hasHighPayWinningSymbol ? 5200 : 1800
                this._fallbackOutWaitTimeoutId = window.setTimeout(() => {
                    if (flowToken !== this._flowToken) {
                        return
                    }

                    this.onRegularWinOutAnimationsComplete()
                }, fallbackMs)
            }

            GameSignals.showRegularWin.emit(data)

            if (!hasOutAnimationWinningSymbol) {
                this._completeWinFlowDelayTween = gsap.delayedCall(1.5, () => {
                    if (flowToken !== this._flowToken) {
                        return
                    }

                    this.completeWinFlow()
                })
            }
        })
    }

    private onRegularWinOutAnimationsComplete = () => {
        if (!this._awaitingOutAnimations) {
            return
        }

        this._awaitingOutAnimations = false
        this.clearPendingCallbacks()

        this.completeWinFlow()
    }

    private onSkipWinPresentation = () => {
        if (this._showRegularWinDelayTween) {
            this._showRegularWinDelayTween.progress(1)
        }

        // Skip only ends the current total-win / symbol presentation.
        // Payline rotation must still start once the game reaches idle.
        this.completeWinFlow()
    }

    private completeWinFlow = (skipLineByLine: boolean = false) => {
        if (this._isWinFlowCompleted) {
            return
        }

        this._isWinFlowCompleted = true
        this.clearPendingCallbacks()
        GameSignals.showWinningsComplete.emit()
        if (skipLineByLine) {
            GameSignals.showLineByLineSkipped.emit()
        }
        this.end()
    }

    private clearPendingCallbacks = () => {
        if (this._fallbackOutWaitTimeoutId !== null) {
            window.clearTimeout(this._fallbackOutWaitTimeoutId)
            this._fallbackOutWaitTimeoutId = null
        }

        if (this._showRegularWinDelayTween) {
            this._showRegularWinDelayTween.kill()
            this._showRegularWinDelayTween = null
        }

        if (this._completeWinFlowDelayTween) {
            this._completeWinFlowDelayTween.kill()
            this._completeWinFlowDelayTween = null
        }
    }

    private setSoundIndexes = (data: ShowRegularWinningsData) => {
        this._model.combinationsData = this.checkCombinations(data)

        if (this._model.combinationsData.isWild) {
            return
        }

        if (!this._model.combinationsData.isWild && this._model.combinationsData.isHP) {
            let newHPIndex: number = Utilities.getRandomInt(0, 2)

            while (SoundManager.hpWinSoundIndex === newHPIndex) {
                newHPIndex = Utilities.getRandomInt(0, 2)
            }

            SoundManager.hpWinSoundIndex = newHPIndex

            return
        }

        if (
            !this._model.combinationsData.isWild &&
            !this._model.combinationsData.isHP &&
            this._model.combinationsData.isLP
        ) {
            let newLPIndex: number = Utilities.getRandomInt(0, 2)

            while (SoundManager.lpWinSoundIndex === newLPIndex) {
                newLPIndex = Utilities.getRandomInt(0, 2)
            }

            SoundManager.lpWinSoundIndex = newLPIndex

            return
        }
    }

    private checkCombinations = (data: ShowRegularWinningsData): CombinationsData => {
        const combinations: WinCombination[] = data.combinations
        let isWild: boolean = false
        let isHP: boolean = false
        let isLP: boolean = false

        for (const combination of combinations) {
            for (const position of combination.symbols_positions) {
                const slotGrid: SymbolData[][] = this._model.spinData.slot_grid[this._model.cascadeIndex]

                if (slotGrid[position[0]][position[1]].id === SpecialSymbolType.MULTIPLIER) {
                    isWild = true
                    break
                }
            }

            if (
                +combination.symbol_id === SymbolType.HP_1 ||
                +combination.symbol_id === SymbolType.HP_2 ||
                +combination.symbol_id === SymbolType.HP_3 ||
                +combination.symbol_id === SymbolType.HP_4
            ) {
                isHP = true
            }

            if (
                +combination.symbol_id === SymbolType.LP_1 ||
                +combination.symbol_id === SymbolType.LP_2 ||
                +combination.symbol_id === SymbolType.LP_3 ||
                +combination.symbol_id === SymbolType.LP_4 ||
                +combination.symbol_id === SymbolType.LP_5
            ) {
                isLP = true
            }
        }

        return {
            isWild,
            isHP,
            isLP,
        }
    }

    public cleanUp() {
        this._flowToken++
        this.clearPendingCallbacks()
        this._isWinFlowCompleted = false
        this._awaitingOutAnimations = false
        GameSignals.regularWinOutAnimationsComplete.disconnect(this.onRegularWinOutAnimationsComplete)
        GameSignals.stopButtonClicked.disconnect(this.onSkipWinPresentation)
        GameSignals.spinButtonClicked.disconnect(this.onSkipWinPresentation)
    }
}
